import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import * as Icons from "lucide-react";
import { Vault as VaultIcon, Package, ArrowUpFromLine, TrendingUp } from "lucide-react";

function toPascal(s){return (s||"").split("-").map(w=>w.charAt(0).toUpperCase()+w.slice(1)).join("");}
const RARITY = { Junk:"#8892a6", Common:"#8892a6", Uncommon:"#2D9CFF", Rare:"#B14DFF", "Ultra-Rare":"#FF2D55", Legendary:"#FFC531" };
const TIER_LABEL = ["Basic", "Silver", "Gold"];

export default function Vault() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/vault").then(r => setD(r.data));
  useEffect(() => { load(); }, []);

  const withdraw = async (it) => {
    setBusy(it.id);
    try { await api.post("/vault/withdraw", { pet_id: it.id }); playSfx("pop"); toast.success(`${it.name} returned to inventory`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  if (!d) return <PageLoader label="Unsealing the vault" />;
  const pct = Math.min(100, (d.used / (d.capacity || 1)) * 100);

  return (
    <div data-testid="vault-page">
      <span className="label-cap text-primary flex items-center gap-2"><VaultIcon className="w-4 h-4" /> Secure Vault Storage</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-1 font-head">My Vault</h1>
      <p className="text-muted-foreground mb-5">Store gear here to keep it out of your 50-item inventory cap. Vault size grows with your bank tier — grow your Vault by banking more Credits.</p>

      <div className="panel-glow p-5 hud-corner mb-6" data-testid="vault-meter">
        <div className="flex items-center justify-between mb-2">
          <span className="label-cap flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-secondary" /> {TIER_LABEL[d.tier]} Tier</span>
          <span className="mono text-sm font-bold">{d.used} / {d.capacity} slots</span>
        </div>
        <div className="h-3 rounded bg-muted overflow-hidden border border-border"><div className="h-full transition-[width] duration-500" style={{ width: `${pct}%`, background: "hsl(var(--primary))" }} /></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {d.items.length === 0 && <div className="col-span-full panel p-8 text-center text-muted-foreground">Vault empty. Store gear from your inventory at the Bazaar.</div>}
        {d.items.map((it) => {
          const Icon = Icons[toPascal(it.icon)] || Package;
          const rc = RARITY[it.rarity] || "#8892a6";
          return (
            <div key={it.id} data-testid={`vaultitem-${it.id}`} className="panel p-4 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <span className="w-10 h-10 rounded-[var(--radius)] grid place-items-center" style={{ background: `${rc}22`, color: rc }}><Icon className="w-5 h-5" /></span>
                <span className="label-cap text-[9px]" style={{ color: rc }}>{it.rarity} ·x{it.qty}</span>
              </div>
              <h3 className="font-head font-bold text-sm">{it.name}</h3>
              <p className="text-xs text-muted-foreground flex-1 mt-1 leading-snug">{it.desc}</p>
              <button onClick={() => withdraw(it)} data-testid={`withdraw-${it.id}`} disabled={busy===it.id} className="btn-primary w-full mt-3 text-xs">
                {busy===it.id ? <Spinner className="w-4 h-4" /> : <><ArrowUpFromLine className="w-4 h-4" /> Withdraw</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
